import * as React from "react";
import { Copy, KeyRound } from "lucide-react";

import { createPersonalToken, listPersonalTokens, revokePersonalToken } from "@/api/client";
import { PersonalTokenView } from "@/api/types";

/**
 * Personal access tokens (MCP 3, #84): the settings surface for the credential an external MCP
 * client holds.
 *
 * <p>Three states drive the whole panel: the list (name, created, last used, revoke — last used
 * is the row that earns the screen), the create flow (a name, an optional write scope), and the
 * shown-once panel. The secret arrives in exactly one response and lives in exactly one piece of
 * transient state here; a re-render after dismissal has nothing to show, by construction.</p>
 *
 * <p>The shown-once sentence is deliberate helper text, the one place that rule bends: it states
 * a consequence the user cannot recover from, not how the UI works. Revoking is irreversible too,
 * so it is a named confirmation, the proportionate form of the review-step rule.</p>
 */

/** The write scope is the only choice worth surfacing: propose-only vs propose-and-act. */
const READ_SCOPES = ["cohort:read", "meetings:read"];
const WRITE_SCOPE = "cohort:write";

export function PersonalTokensPanel() {
  const [tokens, setTokens] = React.useState<PersonalTokenView[] | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [name, setName] = React.useState("");
  const [allowConfirm, setAllowConfirm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  /** The one transient home of a just-minted secret. Dismissed = gone. */
  const [issued, setIssued] = React.useState<{ name: string; secret: string } | null>(null);
  const [copied, setCopied] = React.useState(false);
  /** Token id awaiting the named revoke confirmation. */
  const [confirmRevoke, setConfirmRevoke] = React.useState<string | null>(null);

  React.useEffect(() => {
    listPersonalTokens()
      .then(setTokens)
      .catch(() => setTokens([]));
  }, []);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const scopes = allowConfirm ? [...READ_SCOPES, WRITE_SCOPE] : READ_SCOPES;
      const result = await createPersonalToken(name.trim(), scopes);
      setIssued({ name: result.token.name, secret: result.secret });
      setCopied(false);
      setTokens((prev) => [result.token, ...(prev ?? [])]);
      setName("");
      setAllowConfirm(false);
      setCreating(false);
    } catch {
      setError("Could not create the token");
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(token: PersonalTokenView) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const revoked = await revokePersonalToken(token.id);
      setTokens((prev) => (prev ?? []).map((t) => (t.id === revoked.id ? revoked : t)));
    } catch {
      setError("Could not revoke the token");
    } finally {
      setBusy(false);
      setConfirmRevoke(null);
    }
  }

  async function copySecret(secret: string) {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      /* the selectable text below remains the fallback */
    }
  }

  if (tokens === null) return null;

  return (
    <section className="rounded-2xl border-l-4 border-brand-blue bg-muted/20 p-4">
      <h2 className="mb-3 flex items-center gap-2 font-comic-headline text-sm uppercase tracking-wide text-muted-foreground">
        <KeyRound className="h-4 w-4" /> Access tokens
      </h2>

      {issued && (
        <div className="mb-3 space-y-2 rounded-xl border border-brand-blue/30 bg-brand-blue/10 p-3">
          <p className="text-xs font-medium text-foreground">
            Your token “{issued.name}” is ready. Copy it now: it will not be shown again.
          </p>
          <div className="flex items-center gap-2">
            <code
              data-testid="issued-secret"
              className="min-w-0 flex-1 truncate rounded bg-card px-2 py-1 font-mono text-[11px]"
            >
              {issued.secret}
            </code>
            <button
              type="button"
              onClick={() => copySecret(issued.secret)}
              aria-label="Copy token"
              className="inline-flex items-center gap-1 rounded-full border border-brand-blue/30 bg-brand-blue/10 px-2.5 py-1 text-[11px] font-medium text-brand-blue transition hover:bg-brand-blue/20"
            >
              <Copy className="h-3 w-3" /> {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setIssued(null)}
            className="text-[11px] font-medium text-muted-foreground underline-offset-2 hover:underline"
          >
            Done
          </button>
        </div>
      )}

      {tokens.length === 0 && !creating && (
        <p className="text-xs text-muted-foreground">No tokens</p>
      )}

      <ul className="space-y-2">
        {tokens.map((t) => (
          <li key={t.id} className={t.revoked_at ? "opacity-50" : undefined}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-xs font-medium text-foreground">{t.name}</p>
                <p className="truncate font-mono text-[10px] text-muted-foreground">{t.prefix}…</p>
                <p className="text-[10px] text-muted-foreground">
                  {t.revoked_at
                    ? `Revoked ${shortDate(t.revoked_at)}`
                    : t.last_used_at
                      ? `Last used ${shortDate(t.last_used_at)}`
                      : `Created ${shortDate(t.created_at)} · never used`}
                </p>
              </div>
              {!t.revoked_at && confirmRevoke !== t.id && (
                <button
                  type="button"
                  onClick={() => setConfirmRevoke(t.id)}
                  className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition hover:border-destructive/40 hover:text-destructive"
                >
                  Revoke
                </button>
              )}
            </div>
            {confirmRevoke === t.id && (
              <div className="mt-1 flex items-center justify-between gap-2 rounded-lg bg-destructive/10 px-2 py-1.5">
                <span className="text-[11px] text-foreground">Revoke “{t.name}”?</span>
                <span className="flex shrink-0 gap-1.5">
                  <button
                    type="button"
                    onClick={() => onRevoke(t)}
                    disabled={busy}
                    className="rounded-full bg-destructive px-2 py-0.5 text-[11px] font-medium text-destructive-foreground disabled:opacity-50"
                  >
                    Revoke
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmRevoke(null)}
                    className="rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                  >
                    Keep
                  </button>
                </span>
              </div>
            )}
          </li>
        ))}
      </ul>

      {creating ? (
        <form onSubmit={onCreate} className="mt-3 space-y-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="Token name"
            aria-label="Token name"
            className="w-full rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-brand-blue"
          />
          <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <input
              type="checkbox"
              checked={allowConfirm}
              onChange={(e) => setAllowConfirm(e.target.checked)}
              className="h-3.5 w-3.5 accent-brand-blue"
            />
            Can confirm scheduling
          </label>
          <div className="flex gap-1.5">
            <button
              type="submit"
              disabled={busy || !name.trim()}
              className="rounded-full bg-brand-blue px-3 py-1 text-[11px] font-semibold text-brand-blue-foreground disabled:opacity-50"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setName("");
                setError(null);
              }}
              className="rounded-full border border-border px-3 py-1 text-[11px] font-medium text-muted-foreground"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="mt-3 rounded-full border border-border px-3 py-1 text-[11px] font-medium text-muted-foreground transition hover:border-brand-blue/40 hover:text-brand-blue"
        >
          New token
        </button>
      )}

      {error && <p className="mt-2 text-[11px] text-destructive">{error}</p>}
    </section>
  );
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
